import { AnimatePresence, motion } from "framer-motion";
import { passHeaderSvg, svgToDataUrl } from "@/lib/cardArt";
import { PASS_ACCENT, formatDate, formatTime, splitTitle } from "@/lib/event";

function FarmaanStamp() {
  return (
    <motion.div
      className="absolute right-[4cqw] top-[22cqw] z-10 flex h-[25cqw] w-[25cqw] min-h-[84px] min-w-[84px] flex-col items-center justify-center rounded-full border-[3px] border-[var(--navy)] bg-[var(--yellow)] text-[var(--navy)] shadow-[3px_3px_0_#142344]"
      initial={{ scale: 2.6, opacity: 0, rotate: -30 }}
      animate={{ scale: 1, opacity: 1, rotate: 14 }}
      exit={{ scale: 0.4, opacity: 0, rotate: 40 }}
      transition={{ type: "spring", stiffness: 420, damping: 18 }}
      aria-label="Usool manzoor stamp"
      role="img"
      data-testid="farmaan-stamp"
    >
      <span className="absolute inset-[6%] rounded-full border-2 border-dashed border-[var(--pink)]" aria-hidden="true" />
      <span className="font-poster text-[max(13px,4cqw)] leading-none">Usool</span>
      <span className="font-poster text-[max(10px,2.9cqw)] leading-none text-[var(--pink-deep)]">Manzoor!</span>
      <span className="text-[max(10px,2.6cqw)] font-extrabold leading-none" aria-hidden="true">✓ ✓ ✓</span>
    </motion.div>
  );
}

function HostStamp({ hosts }) {
  const words = (hosts || "Admin").trim().split(/\s+/);
  const main = words.pop();
  return (
    <div className="flex h-[25cqw] w-[25cqw] min-h-[88px] min-w-[88px] shrink-0 -rotate-12 flex-col items-center justify-center rounded-full border-[3px] border-[var(--pink)] px-[2cqw] text-center text-[var(--pink)] outline-dashed outline-2 outline-offset-[-9px] outline-[var(--pink)]" aria-label={`${hosts} approved stamp`} data-testid="card-host-stamp">
      {words.length > 0 && <span className="font-poster max-w-[80%] text-[max(9px,2.2cqw)] leading-[1.05]">{words.join(" ")}</span>}
      <span className="font-poster text-[max(14px,4.2cqw)] leading-none">{main}</span>
      <span className="font-poster text-[max(10px,2.6cqw)] leading-none">APPROVED</span>
    </div>
  );
}

export function InviteCard({ ev, name, mood, accepted = false }) {
  const accent = PASS_ACCENT;
  const { l1, l2, last } = splitTitle(ev.title);
  const shortDate = formatDate(ev.date).replace(/^(\w+),/, (m, d) => `${d.slice(0, 3)},`);
  return (
    <div className="relative mx-auto w-full max-w-[420px]" style={{ containerType: "inline-size" }} data-testid="personalized-card-preview">
      <div className="relative overflow-hidden rounded-[22px] border-[3px] border-[var(--navy)] bg-[var(--cream)] shadow-[7px_7px_0_#142344]">
        <img src={svgToDataUrl(passHeaderSvg(accent.hex))} alt="" className="block w-full" />
        <AnimatePresence>{accepted && <FarmaanStamp key="stamp" />}</AnimatePresence>
        <div className="px-[7cqw] pb-[5cqw] pt-[4cqw] text-center">
          <p className="text-[max(11px,2.4cqw)] font-extrabold uppercase tracking-[0.2em] text-[var(--pink-deep)]">{ev.opening_eyebrow}</p>
          <p className="font-poster mt-[2cqw] text-[max(30px,11cqw)] leading-[0.92] text-[var(--navy)]">
            <span className="block">{l1}</span>
            <span className="block">{l2} <span className="font-serif-i text-[1.12em] text-[var(--pink)]">{last}</span></span>
          </p>
          <p className="mt-[4cqw] text-[max(11px,2.2cqw)] font-extrabold tracking-[0.3em]">ADMIT</p>
          <p className="font-serif-i mt-[0.5cqw] text-[max(26px,8cqw)] leading-[1] text-[var(--navy)] [overflow-wrap:anywhere]" data-testid="card-guest-name">{name || "Our favourite guest"}</p>
          <div className="mt-[4cqw] grid grid-cols-2 border-t-[2px] border-[var(--navy)] pt-[3cqw]">
            <div className="border-r-[2px] border-[var(--navy)] pr-2">
              <p className="text-[max(11px,2cqw)] font-extrabold tracking-[0.18em] text-[var(--pink-deep)]">KAB?</p>
              <p className="font-poster text-[max(15px,4.2cqw)] leading-tight">{shortDate}</p>
            </div>
            <div className="pl-2">
              <p className="text-[max(11px,2cqw)] font-extrabold tracking-[0.18em] text-[var(--pink-deep)]">KITNE BAJE?</p>
              <p className="font-poster text-[max(15px,4.2cqw)] leading-tight">{formatTime(ev.start_time)}</p>
            </div>
          </div>
          <p className="mt-[3cqw] text-[max(11px,2cqw)] font-extrabold tracking-[0.18em] text-[var(--pink-deep)]">KAHAN?</p>
          <p className="font-poster text-[max(15px,4.2cqw)] leading-tight">{ev.venue_title}</p>
        </div>
        <div className="relative mx-[5cqw] border-t-[3px] border-dashed border-[var(--navy)]" aria-hidden="true">
          <span className="absolute -left-[calc(5cqw+16px)] -top-[16px] h-[30px] w-[30px] rounded-full border-[3px] border-[var(--navy)] bg-[var(--cream)]" />
          <span className="absolute -right-[calc(5cqw+16px)] -top-[16px] h-[30px] w-[30px] rounded-full border-[3px] border-[var(--navy)] bg-[var(--cream)]" />
        </div>
        <div className="flex items-center gap-[3cqw] px-[6cqw] pb-[6cqw] pt-[4cqw]">
          <div className="relative flex-1 overflow-hidden rounded-[14px] border-[2.5px] border-[var(--navy)] py-[2.5cqw] pl-[6cqw] pr-[3cqw] text-left transition-colors duration-500" style={{ background: accent.soft }} data-testid="card-mood-box">
            <span className="absolute inset-y-0 left-0 w-[3cqw] transition-colors duration-500" style={{ background: accent.hex }} aria-hidden="true" />
            <p className="text-[max(11px,2cqw)] font-extrabold tracking-[0.18em] text-[var(--pink-deep)]">{mood ? "FESTIVE MOOD" : "THE VIBE"}</p>
            <p className="font-poster text-[max(15px,4.3cqw)] leading-tight">{mood ? mood.name : ev.opening_line}</p>
            {mood && <p className="text-[max(11px,2.3cqw)] leading-snug">{mood.line}</p>}
          </div>
          <HostStamp hosts={ev.host_names} />
        </div>
      </div>
    </div>
  );
}
